import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin, Calendar, Building } from 'lucide-react';
import { format } from 'date-fns';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import JobApplicationDialog from '@/components/careers/JobApplicationDialog';

interface JobOpening {
  id: string;
  title: string;
  location: string;
  department: string;
  employment_type: string;
  description: string;
  requirements: string;
  benefits: string;
  posted_date: string;
  application_deadline: string | null;
}

const CareersPage: React.FC = () => {
  const { t } = useTranslation(['careers', 'common']);
  const [jobs, setJobs] = useState<JobOpening[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState<JobOpening | null>(null);
  const [showApplicationDialog, setShowApplicationDialog] = useState(false);

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const { data, error } = await supabase
        .from('job_openings')
        .select('*')
        .eq('is_active', true)
        .order('posted_date', { ascending: false });

      if (error) throw error;
      setJobs(data || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getEmploymentTypeColor = (type: string) => {
    switch (type) {
      case 'full_time':
        return 'bg-primary text-primary-foreground';
      case 'part_time':
        return 'bg-secondary text-secondary-foreground';
      case 'contract':
        return 'bg-accent text-accent-foreground';
      case 'internship':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const handleApplyClick = (job: JobOpening) => {
    setSelectedJob(job);
    setShowApplicationDialog(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">{t('common:loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow bg-amber-50/50 pt-16">
        {/* Jobs Section */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold text-foreground mb-4">
                {t('careers:openPositions')}
              </h1>
              <p className="text-muted-foreground">
                {t('careers:openPositionsDesc')}
              </p>
            </div>

          {jobs.length === 0 ? (
            <Card className="max-w-2xl mx-auto">
              <CardContent className="text-center py-12">
                <p className="text-lg text-muted-foreground mb-4">
                  {t('careers:noPositions')}
                </p>
                <p className="text-muted-foreground">
                  {t('careers:checkBackLater')}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {jobs.map((job) => (
                <Card key={job.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex justify-between items-start mb-2">
                      <CardTitle className="text-lg">{job.title}</CardTitle>
                      <Badge className={getEmploymentTypeColor(job.employment_type)}>
                        {t(`careers:employmentTypes.${job.employment_type}`)}
                      </Badge>
                    </div>
                    <CardDescription className="space-y-2">
                      <div className="flex items-center text-sm text-muted-foreground">
                        <MapPin className="w-4 h-4 mr-2" />
                        {job.location}
                      </div>
                      {job.department && (
                        <div className="flex items-center text-sm text-muted-foreground">
                          <Building className="w-4 h-4 mr-2" />
                          {job.department}
                        </div>
                      )}
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Calendar className="w-4 h-4 mr-2" />
                        {t('careers:postedOn', { date: format(new Date(job.posted_date), 'MMM dd, yyyy') })}
                      </div>
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                      {job.description.length > 150 
                        ? `${job.description.substring(0, 150)}...` 
                        : job.description}
                    </p>
                    <Button 
                      onClick={() => handleApplyClick(job)}
                      className="w-full"
                    >
                      {t('careers:applyNow')}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
      </main>
      <Footer />

      {/* Application Dialog */}
      {selectedJob && (
        <JobApplicationDialog
          job={selectedJob}
          open={showApplicationDialog}
          onOpenChange={setShowApplicationDialog}
          onSuccess={() => {
            setShowApplicationDialog(false);
            setSelectedJob(null);
          }}
        />
      )}
    </div>
  );
};

export default CareersPage;